/**
 * The project's MCP tab (vfs-service src/mcptester, in the pod where the project runs): the MCP servers its services
 * expose, a connection to one (its tools, resources and prompts with their schemas), and one method called on it, from
 * the server to the running service through the preview. The same authorized fetch as the API tab
 * (services/ApiTesterService.js); the person's Flow sign-in stays with vfs-service, never sent on to the app.
 *
 * - `getMcpServers(projectId)` → `{ canWrite, count, auth, services: [{ service, name, running, state, previewUrl,
 *   servers: [{ path, transport, source, sdk, language, file, line, guessed?, note? }] }] }`
 * - `connectMcp(projectId, { service, path, transport?, as })` → `{ transport, protocolVersion, server, capabilities,
 *   instructions, tools, resources, resourceTemplates, prompts, errors, ms, exchanges, as }`
 * - `callMcp(projectId, { service, path, transport?, method, params?, as })` → `{ method, result | error, truncated?,
 *   ms, logs, exchanges, transport, as }`
 * - `startMcpOAuth(projectId, { service, path, redirectUri, resourceMetadata?, clientId? })` → `{ authorizationUrl,
 *   state, authorizationServer, registered }`; `finishMcpOAuth(projectId, { state, code })` → `{ accessToken,
 *   tokenType, expiresIn, scope }` ("Sign in with OAuth")
 *
 * A server that wants a sign-in rejects with code `mcp_auth` and `body.signIn` (the options that apply).
 */
import config from '../config';
import { getAuthorizedFetch } from '../utils/fetchRegistry';
import { buildAclContextHeaders } from '../utils/aclContext';
import { ApiTesterError } from './ApiTesterService';

const base = () => (config.vfsServiceUrl || '').replace(/\/$/, '');
const mcpUrl = (projectId, path) => `${base()}/api/v1/projects/${encodeURIComponent(projectId)}/mcp${path}`;

async function call(url, { method = 'GET', body } = {}) {
  if (!base()) { throw new ApiTesterError('vfs-service is not configured', 0, null); }
  const json = body !== undefined;
  const response = await getAuthorizedFetch()(url, {
    method,
    headers: { Accept: 'application/json', ...buildAclContextHeaders(), ...(json ? { 'Content-Type': 'application/json' } : {}) },
    ...(json ? { body: JSON.stringify(body) } : {}),
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) { throw new ApiTesterError(data?.error || data?.message || `Request failed (${response.status})`, response.status, data); }
  return data || {};
}

export function getMcpServers(projectId) {
  return call(mcpUrl(projectId, '/servers'));
}

export function connectMcp(projectId, input) {
  return call(mcpUrl(projectId, '/connect'), { method: 'POST', body: input });
}

export function callMcp(projectId, input) {
  return call(mcpUrl(projectId, '/call'), { method: 'POST', body: input });
}

export function startMcpOAuth(projectId, input) {
  return call(mcpUrl(projectId, '/oauth/start'), { method: 'POST', body: input });
}

export function finishMcpOAuth(projectId, input) {
  return call(mcpUrl(projectId, '/oauth/finish'), { method: 'POST', body: input });
}
